import { forwardRef, ReactNode, FocusEvent, useState, useEffect } from 'react'

import { cva, VariantProps } from 'class-variance-authority'

import { MaskedNumber } from 'imask'

import { mergeClassName } from '@erudilabs/merge-class-utils'

const number = cva(
  [
    'border w-full transition-all h-10 rounded-md text-sm pl-4 pr-4 placeholder-gray-600',
    'bg-white border-gray-300 text-black focus:outline-blue-500',
    'dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:focus:outline-blue-300',
  ],
  {
    variants: {
      disabled: {
        true: [
          'cursor-not-allowed',
          'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-200 disabled:placeholder-gray-500',
          'dark:bg-gray-800 dark:border-gray-800 dark:text-gray-600 dark:hover:bg-gray-800 dark:disabled:placeholder-gray-700',
        ],
      },
    },
    defaultVariants: {
      disabled: false,
    },
  }
)

export interface NumberInputProps extends VariantProps<typeof number> {
  className?: string
  testId?: string
  id?: string
  label?: string
  prefix?: ReactNode
  suffix?: ReactNode
  name: string
  helper?: ReactNode
  max?: number
  defaultValue?: string
  value?: string
  readOnly?: boolean
  placeholder?: string
  errorMessage?: string
  hideErrorMessage?: boolean
  clearable?: boolean
  autoFocus?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (e: any) => void
  onBlur?: (e: FocusEvent) => void
}

export const maskInputOnlyNumber = (str?: string | null) => {
  if (!str) return undefined
  const masking = new MaskedNumber({
    mask: Number,
    normalizeZeros: false,
    signed: false,
  })
  return masking.resolve(str)
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (props, ref) => {
    const {
      readOnly,
      name,
      testId,
      id,
      max,
      onChange,
      onBlur,
      defaultValue,
      value,
      prefix,
      suffix,
      errorMessage,
      hideErrorMessage,
      placeholder,
      helper,
      autoFocus,
      label,
      disabled,
      className,
    } = props

    const [maskedValue, setMaskedValue] = useState<number | string>(
      value || defaultValue || ''
    )

    const handleChange = (str: string) => {
      const masked = maskInputOnlyNumber(str)
      onChange?.(masked || '')
      setMaskedValue(masked || '')
    }

    useEffect(() => {
      if (value !== maskedValue) {
        setMaskedValue(value as string)
      }
    }, [value])

    return (
      <div className={mergeClassName('relative', className)}>
        {prefix && (
          <div
            className={`${
              label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2'
            } absolute left-4`}
          >
            {prefix}
          </div>
        )}
        {suffix && (
          <div
            className={`${
              label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2'
            } absolute right-4`}
          >
            {suffix}
          </div>
        )}
        {label && (
          <label
            htmlFor={name}
            className={mergeClassName(
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-black dark:text-white',
              'mb-2 block cursor-pointer text-left font-semibold'
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          maxLength={max}
          readOnly={readOnly}
          data-testid={testId}
          value={maskedValue}
          name={name}
          placeholder={placeholder}
          autoFocus={autoFocus}
          type="tel"
          className={mergeClassName(
            number({
              disabled,
            }),
            errorMessage && 'border-red-500 dark:border-red-400',
            suffix && 'pr-11',
            prefix && 'pl-11'
          )}
          disabled={disabled as boolean}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          autoComplete="off"
        />
        {helper && (
          <p className="mt-2 text-left text-xs font-semibold text-gray-600 first-letter:capitalize">
            {helper}
          </p>
        )}
        {errorMessage && !hideErrorMessage && (
          <p className="mt-2 text-left text-xs font-semibold text-red-600 dark:text-red-400 first-letter:capitalize">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
